import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { ResolutionsContext } from './resolutions-context';
import { ResolutionConfig } from '../shared/types/binding-types';

type ResolutionsProviderProps = {
  children: ReactNode;
  resolutions: ResolutionConfig[];
  defaultResolutionId?: string;
  activeResolutionId?: string;
  onActiveResolutionChange?: (id: string) => void;
};

export const ResolutionsProvider: React.FC<ResolutionsProviderProps> = ({
  children,
  resolutions: initialResolutions,
  defaultResolutionId,
  activeResolutionId: controlledActiveId,
  onActiveResolutionChange
}) => {
  const [resolutions, setResolutions] = useState(initialResolutions);
  const [internalActiveId, setInternalActiveId] = useState(
    controlledActiveId ?? defaultResolutionId ?? initialResolutions[0]?.id ?? ''
  );

  useEffect(() => {
    if (controlledActiveId !== undefined) setInternalActiveId(controlledActiveId);
  }, [controlledActiveId]);

  const activeResolutionId = controlledActiveId ?? internalActiveId;

  const setActiveResolutionId = useCallback(
    (id: string) => {
      if (controlledActiveId === undefined) setInternalActiveId(id);
      onActiveResolutionChange?.(id);
    },
    [controlledActiveId, onActiveResolutionChange]
  );

  const activeResolution = useMemo(
    () => resolutions.find(r => r.id === activeResolutionId) ?? resolutions[0],
    [resolutions, activeResolutionId]
  );

  const value = useMemo(
    () => ({ resolutions, activeResolution, setActiveResolutionId, setResolutions }),
    [resolutions, activeResolution, setActiveResolutionId]
  );

  return <ResolutionsContext.Provider value={value}>{children}</ResolutionsContext.Provider>;
};
